
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { UserRoles } from '../auth/enums';

interface AdminContextType {
  isAdminArea: boolean;
  setIsAdminArea: (value: boolean) => void;
  adminSection: string;
  setAdminSection: (section: string) => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAdminArea, setIsAdminArea] = useState(false);
  const [adminSection, setAdminSection] = useState('dashboard');
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  // Check if user has admin access
  useEffect(() => {
    if (isAuthenticated && user) {
      const hasAdminAccess = user.roles.includes(UserRoles.ADMIN);
      
      if (isAdminArea && !hasAdminAccess) {
        setIsAdminArea(false);
        toast({
          title: "Hozzáférés megtagadva",
          description: "Nincs jogosultságod az admin felület használatához.",
          variant: "destructive",
        });
      }
    } else if (isAdminArea) {
      setIsAdminArea(false);
    }
  }, [isAuthenticated, user, isAdminArea, toast]);

  const value = {
    isAdminArea,
    setIsAdminArea,
    adminSection,
    setAdminSection,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
